import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { StoryLabelsStateModel } from './story.labels.state.model';
import { 
  AddLabel,
  RemoveLabel,
  SetLabels,
  SelectLabel,
  SetLabel,
  RemoveAllChapterLabels,
  CopyLabels,
  RemoveAllLabels,
  ResetLabels,
  ConvertLabels,
  AddContextLabel,
  EnableContextLabel,
  RemoveContextLabel,
  SetLabelsShown,
  SetAllLabelsShown,
  SetLabelsCollapsed,
  SetLabelsDraggable} from './story.labels.actions';
import { uuid } from 'src/app/shared/uuid';
import { StoryChaptersState } from '../chapters/story.chapters.state';
import { StoryChaptersStateModel } from '../chapters/story.chapters.state.model';
import { LabelsService } from '../../labels.service';
import { LabelRecipe } from '../../models/labels/label-recipe.enum';
import { FlyToChapterErrandModel } from '../../models/errands/fly-to-chapter-errand.model';
import { FlyToObjectErrandModel } from '../../models/errands/fly-to-object-errand.model';
import { cloneDeep } from 'lodash';
import { LabelCollapseModeEnum } from '../../models/labels/label-collapse-mode';
import { EventsEnum } from 'src/app/engine/models/events.enum';

declare const Human: any;

const initialState: StoryLabelsStateModel = {
  enableContextLabel: {
    enable: true,
    enableInfo: true,
    enableActions: true
  },
  loading: false,
  labelsCollapsed: false,
  labelsCollapsedMode: LabelCollapseModeEnum.None,
  labels: [],
  contextLabel: null,
  selectedLabel: null,
  draggable: {
    pin: true,
    tag: true
  }
};

@State<StoryLabelsStateModel>({
  name: 'labels',
  defaults: initialState
})
export class StoryLabelsState {
  /**
   * Selectors
   */
  @Selector()
  static getLabels(state: StoryLabelsStateModel) {
    return state.labels;
  }

  @Selector()
  static getLabelsCollapsed(state: StoryLabelsStateModel) {
    return state.labelsCollapsed;
  }

  @Selector()
  static getLabelsCollapsedMode(state: StoryLabelsStateModel) {
    return state.labelsCollapsedMode;
  }

  @Selector()
  static getSelectedLabelId(state: StoryLabelsStateModel) {
    return state.selectedLabel;
  }

  @Selector()
  static getSelectedLabel(state: StoryLabelsStateModel) {
    return state.labels.find(label => label.id === state.selectedLabel) || null;
  }

  @Selector()
  static getContextLabel(state: StoryLabelsStateModel) {
    return state.contextLabel;
  }

  @Selector([StoryChaptersState])
  static getChapterLabels(state: StoryLabelsStateModel, storyChaptersState: StoryChaptersStateModel) {
    return state.labels.filter(label => label.chapterId === storyChaptersState.selectedChapter);
  }

  @Selector()
  static getEnableContextLabel(state: StoryLabelsStateModel) {
    return state.enableContextLabel.enable;
  }

  @Selector()
  static getEnableContextLabelInfo(state: StoryLabelsStateModel) {
    return state.enableContextLabel.enableInfo;
  }

  @Selector()
  static getEnableContextLabelActions(state: StoryLabelsStateModel) {
    return state.enableContextLabel.enableActions;
  }

  @Selector()
  static getLabelsDraggable(state: StoryLabelsStateModel) {
    return state.draggable;
  }

  /**
   * Actions
   */
  @Action(SetLabels)
  setLabels(context: StateContext<StoryLabelsStateModel>, action: SetLabels) {
    context.patchState({
      labels: action.labels
    });
  }

  @Action(SetLabel)
  setLabel(context: StateContext<StoryLabelsStateModel>, action: SetLabel) {
    const labels = context.getState().labels;
    const labelIndex = labels.findIndex((_label) => _label.id === action.label.id);
    labels[labelIndex] = { ...action.label };
    
    context.patchState({
      labels: [...labels]
    });
  }

  @Action(AddLabel)
  addLabel(context: StateContext<StoryLabelsStateModel>, action: AddLabel) {
    action.label.id = action.label.id || uuid.idWithPrefix('label');

    const labels = context.getState().labels;
    context.patchState({
      labels: [...labels, {...action.label}]
    });
  }

  @Action(AddContextLabel)
  addContextLabel(context: StateContext<StoryLabelsStateModel>, action: AddContextLabel) {
    const label = {...action.label};
    label.id = 'context-label';

    context.patchState({
      contextLabel: label
    });
  }


  @Action(CopyLabels)
  copyLabels(context: StateContext<StoryLabelsStateModel>, action: CopyLabels) {
    const labels = context.getState().labels;
    const sourceLabels = labels.filter(label => label.chapterId === action.sourceChapter.id);
    const targetLabels = sourceLabels.map(label => {
      return {
        ...(cloneDeep(label)),
        id: uuid.idWithPrefix('label'),
        chapterId: action.targetChapter.id
      };
    });

    context.patchState({
      labels: [...labels, ...targetLabels]
    });
  }
  
  @Action(RemoveLabel)
  removeLabel(context: StateContext<StoryLabelsStateModel>, action: RemoveLabel) {
    const labels = context.getState().labels
      .filter((label) => {
        return label.id !== action.label.id;
      });

    context.patchState({
      labels: [...labels]
    });

    Human.scene.markers.delete(action.label.id);
  }

  @Action(RemoveContextLabel)
  removeContextLabel(context: StateContext<StoryLabelsStateModel>, action: RemoveContextLabel) {
    context.patchState({
      contextLabel: null
    });

    Human.scene.markers.delete('context-label');
  }

  @Action(RemoveAllLabels) 
  removeAllLabels(context: StateContext<StoryLabelsStateModel>, action: RemoveLabel) {
    const labels = context.getState().labels;

    // Remove labels from the engine
    labels.forEach((label) => {
      Human.scene.markers.delete(label.id);
    });

    // Update application state
    context.patchState({
      labels: []
    });
  }

  @Action(RemoveAllChapterLabels)
  removeAllChapterLabels(context: StateContext<StoryLabelsStateModel>, action: RemoveAllChapterLabels) {
    const labels = context.getState().labels;
    const chapterLabels = labels.filter(label => label.chapterId === action.chapter.id);
    const filteredLabels = labels.filter(label => label.chapterId !== action.chapter.id);

    // Remove labels from the engine
    chapterLabels.forEach((label) => {
      Human.scene.markers.delete(label.id);
    });

    // Update application state
    context.patchState({
      labels: [...filteredLabels]
    });
  }

  @Action(SelectLabel)
  selectLabel(context: StateContext<StoryLabelsStateModel>, action: SelectLabel) {
    context.patchState({
      selectedLabel: action.label ? action.label.id : null
    });
  }

  @Action(ResetLabels)
  resetLabels(context: StateContext<StoryLabelsStateModel>, action: ResetLabels) {
    context.dispatch(new RemoveAllLabels());
    context.dispatch(new RemoveContextLabel());
    context.setState(initialState);
  }

  @Action(ConvertLabels)
  convertLabels(context: StateContext<StoryLabelsStateModel>, action: ConvertLabels) {
    const labels = context.getState().labels;
    action.labels.forEach(label => {
      const index = labels.findIndex(_label => _label.id === label.id);

      switch (action.type) {
        case LabelRecipe.StudioNext:
          labels[index] = this.labelsService.makeDefaultStudioLabel(labels[index]);

          if (label.objectId) {
            labels[index].states[0].errands[0] = new FlyToChapterErrandModel();
            labels[index].states[1].errands[0] = new FlyToObjectErrandModel(label.objectId);  
          }

          break;
        case LabelRecipe.Annotation:
          labels[index] = this.labelsService.makeAnnotationLabel(labels[index]);
          break;
      }
    });

    context.patchState({
      labels: [...labels]
    });
  }

  @Action(EnableContextLabel)
  enableContextLabel(context: StateContext<StoryLabelsStateModel>, action: EnableContextLabel) {
    context.patchState({
      enableContextLabel: {
        ...action
      }
    });
  }
  
  @Action(SetLabelsShown)
  setLabelsShown(context: StateContext<StoryLabelsStateModel>, { labels: actionLabels, shown }: SetLabelsShown) {
    // set shown state for each update label
    const labels = context.getState().labels
      .map((label) => {
        const doUpdate = actionLabels.some((actionLabel) => actionLabel.id === label.id);
        return {
          ...label,
          shown: doUpdate ? shown : label.shown
        };
      });

    context.patchState({
      labels: [...labels]
    });
  }

  @Action(SetAllLabelsShown)
  setAllLabelsShown(context: StateContext<StoryLabelsStateModel>, { shown }: SetAllLabelsShown) {
    const labels = context.getState().labels;
    context.dispatch(new SetLabelsShown(labels, shown));
  }

  
  @Action(SetLabelsCollapsed)
  setLabelsCollapsed(context: StateContext<StoryLabelsStateModel>, { collapsed, collapsedMode: mode }: SetLabelsCollapsed) {
    context.patchState({
      labelsCollapsed: collapsed,
      labelsCollapsedMode: mode
    });
  }

  @Action(SetLabelsDraggable)
  setLabelsDraggable(context: StateContext<StoryLabelsStateModel>, action: SetLabelsDraggable) {
    context.patchState({
      draggable: {...action.draggable}
    });
  }

  constructor(
    private labelsService: LabelsService
  ) { }
}
